import React from 'react';
import { Box, Typography, Chip, Tooltip } from '@mui/material';
import { formatDistanceToNow } from 'date-fns';
import ActivityIcon from './ActivityIcon';

/**
 * Get a user-friendly label for activity category
 */
const getCategoryLabel = (category) => {
  const labels = {
    FLEET: 'Fleet',
    TEAM: 'Team',
    MISSION: 'Mission',
    LAANC: 'LAANC',
    ORGANIZATION: 'Organization',
    FILES: 'Files',
    COMPLIANCE: 'Compliance'
  };
  return labels[category] || category;
};

/**
 * Get chip color based on severity
 */
const getSeverityColor = (severity) => {
  switch (severity) {
    case 'ERROR':
      return 'error';
    case 'WARNING':
      return 'warning';
    case 'INFO':
    default:
      return 'default';
  }
};

/**
 * ActivityItem component - renders a single activity in the feed
 */
const ActivityItem = ({ activity, showCategory = true, compact = false }) => {
  const {
    activityType,
    category,
    description,
    user,
    createdAt,
    severity,
    entityName,
    metadata
  } = activity;

  // Format timestamp
  const timeAgo = formatDistanceToNow(new Date(createdAt), { addSuffix: true });

  // Extract user name
  const userName = user
    ? `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email
    : 'System';

  return (
    <Box
      sx={{
        display: 'flex',
        gap: { xs: 1, sm: 2 },
        padding: { xs: 1, sm: compact ? 1.5 : 2 },
        borderBottom: '1px solid #e0e0e0',
        '&:hover': {
          backgroundColor: '#f5f5f5'
        },
        transition: 'background-color 0.2s'
      }}
    >
      {/* Icon */}
      <Box sx={{ flexShrink: 0 }}>
        <ActivityIcon
          activityType={activityType}
          category={category}
          severity={severity}
          size={compact ? 'small' : 'medium'}
        />
      </Box>

      {/* Content */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        {/* Header - User and Time */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: { xs: 0.5, sm: 1 },
            marginBottom: 0.5,
            flexWrap: 'wrap'
          }}
        >
          <Typography
            variant="body2"
            sx={{
              fontWeight: 600,
              color: '#424242',
              fontSize: { xs: '0.813rem', sm: '0.875rem' }
            }}
          >
            {userName}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: '#757575',
              fontSize: { xs: '0.7rem', sm: '0.75rem' }
            }}
          >
            {timeAgo}
          </Typography>
          {showCategory && (
            <Chip
              label={getCategoryLabel(category)}
              size="small"
              sx={{
                height: { xs: 18, sm: 20 },
                fontSize: { xs: '0.65rem', sm: '0.7rem' },
                fontWeight: 500
              }}
            />
          )}
          {severity !== 'INFO' && (
            <Chip
              label={severity}
              size="small"
              color={getSeverityColor(severity)}
              sx={{
                height: { xs: 18, sm: 20 },
                fontSize: { xs: '0.65rem', sm: '0.7rem' },
                fontWeight: 500
              }}
            />
          )}
        </Box>

        {/* Description */}
        <Typography
          variant="body2"
          sx={{
            color: '#616161',
            wordBreak: 'break-word',
            marginBottom: entityName ? 0.5 : 0,
            fontSize: { xs: '0.813rem', sm: '0.875rem' }
          }}
        >
          {description}
        </Typography>

        {/* Entity Name (if available) */}
        {entityName && (
          <Typography
            variant="caption"
            sx={{
              color: '#9e9e9e',
              display: 'block'
            }}
          >
            Entity: {entityName}
          </Typography>
        )}

        {/* Metadata (optional, shown on hover) */}
        {metadata && Object.keys(metadata).length > 0 && !compact && (
          <Tooltip
            title={
              <Box>
                <Typography variant="caption" sx={{ fontWeight: 600 }}>
                  Additional Details:
                </Typography>
                <Box component="pre" sx={{ fontSize: '0.7rem', margin: 0.5 }}>
                  {JSON.stringify(metadata, null, 2)}
                </Box>
              </Box>
            }
            arrow
          >
            <Typography
              variant="caption"
              sx={{
                color: '#1976d2',
                cursor: 'pointer',
                display: 'inline-block',
                marginTop: 0.5,
                '&:hover': {
                  textDecoration: 'underline'
                }
              }}
            >
              View details
            </Typography>
          </Tooltip>
        )}
      </Box>
    </Box>
  );
};

export default ActivityItem;
